import { useMemo, useState } from "react";

import { NvModal } from "@/components/navigator";
import { cn } from "@/lib/utils";
import type { WorkflowStage, WorkflowStep } from "@/services/navigator/agent-detail-service";

import { NvLucideCloseIcon, NvMaximizeIcon, NvUserSmallIcon } from "./detail-icons";

// 워크플로우 도식 (설계서 slide 45 · 58 · Figma ver.7 03-01 · 03-04 · 03-00): 단계 띠(Stage Pill + 이름 + 설명, 띠 사이 점선) 안에 세부단계 카드를 가로로,
// 사람 개입(확인 · 승인 · 수정)이 있는 단계는 흰 바탕 점선 카드 + 사람 칩(사람 아이콘), 나머지는 주황 테두리.
// 같은 앞 숫자(2-1, 2-2 …)는 하나의 병렬 그룹 박스(2열 격자, 4개를 넘으면 '+N 더보기')로 묶고, 한 단계 흐름이 5칸을 넘으면 가로로 스크롤해요.
// 카드를 누르면 같은 띠 아래에 하는 일 / INPUT / OUTPUT 패널, 제목 줄 오른쪽 [전체 보기]는 모든 세부단계를 한 화면에 펼친 팝업.
// 되돌아감은 카드 아래 작은 글씨로 표시해요(곡선 화살표 대신).

const POPUP_PER_ROW = 4; // 인라인(카드 166) · 전체 보기 팝업은 4개(카드 252)
const GROUP_VISIBLE = 4; // 병렬 그룹은 4개까지 보이고 나머지는 '+N 더보기'

export const isHuman = (step: WorkflowStep) => !!step.hitl && step.hitl !== "없음";

type Cell = { kind: "step"; step: WorkflowStep } | { kind: "group"; major: string; steps: WorkflowStep[] };

const majorOf = (code: string) => (code.includes("-") ? code.split("-")[0] : null);

// 같은 앞 숫자(2-1, 2-2)가 이어지면 병렬 그룹 (둘 이상일 때만), 그 밖에는 카드 한 장
function cellsOf(steps: WorkflowStep[]): Cell[] {
  const out: Cell[] = [];
  for (let i = 0; i < steps.length; ) {
    const major = majorOf(steps[i].code);
    let j = i + 1;
    while (major && j < steps.length && majorOf(steps[j].code) === major) j += 1;
    if (major && j - i >= 2) out.push({ kind: "group", major, steps: steps.slice(i, j) });
    else for (let k = i; k < j; k += 1) out.push({ kind: "step", step: steps[k] });
    i = j;
  }
  return out;
}

// 팝업 줄바꿈: 한 줄에 카드 4칸, 병렬 그룹은 2칸으로 셈해요
function chunkCells(cells: Cell[], size: number): Cell[][] {
  const rows: Cell[][] = [];
  let used = 0;
  for (const cell of cells) {
    const span = cell.kind === "group" ? 2 : 1;
    if (!rows.length || used + span > size) {
      rows.push([]);
      used = 0;
    }
    rows[rows.length - 1].push(cell);
    used += span;
  }
  return rows;
}

// 범례 (Figma ver.7: 13/500 #5E626A, 칸 14 · 반경 4 · 선 1) — 워크플로우 제목 줄 오른쪽
export function WorkflowLegend() {
  return (
    <div className="flex items-center gap-4 text-[13px] font-medium leading-4 text-nv-text-secondary">
      <span className="inline-flex items-center gap-[6px]">
        <span aria-hidden className="h-[14px] w-[14px] rounded-[4px] border border-nv-brand bg-nv-brand-subtle" />
        Agent 수행
      </span>
      <span className="inline-flex items-center gap-[6px]">
        <span aria-hidden className="h-[14px] w-[14px] rounded-[4px] border border-dashed border-[#8E939A] bg-white" />
        사람 개입
      </span>
    </div>
  );
}

// [전체 보기] 99 x 34 · 반경 8 · 선 #CBD1D6 (Figma ver.7)
export function WorkflowFullButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="워크플로우 전체 보기"
      className="inline-flex h-[34px] items-center gap-[5px] rounded-nv-md border border-[#CBD1D6] bg-white pl-[14px] pr-[13px] text-[13px] font-medium leading-4 text-nv-text-primary hover:border-nv-border-strong"
    >
      <NvMaximizeIcon size={16} />
      전체 보기
    </button>
  );
}

function Arrow({ className }: { className?: string }) {
  return (
    <svg aria-hidden width="29" height="12" viewBox="0 0 29 12" fill="none" className={cn("shrink-0 text-[#8E939A]", className)}>
      <path d="M0.763158 5.85088H28.2368M23.1491 10.9386L28.2368 5.85088L23.1491 0.763158" stroke="currentColor" strokeWidth="1.52632" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StepNode({ step, selected, onSelect, wide, compact }: { step: WorkflowStep; selected: boolean; onSelect: () => void; wide?: boolean; compact?: boolean }) {
  const human = isHuman(step);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex flex-col items-start rounded-nv-card border text-left",
        // 카드 높이는 내용에 맞춤 (최소 높이만): 인라인 109 · 팝업 82 · 병렬 그룹 안 73. 인라인 카드는 줄의 칸(격자)을 채워요 (1136 폭에서 166.4)
        wide ? "min-h-[82px] w-[252px] shrink-0 gap-[6px] px-[15px] py-[13px]" : compact ? "min-h-[73px] w-full min-w-0 gap-[6px] px-[15px] py-[13px]" : "min-h-[109px] w-full min-w-0 gap-[6px] px-[13px] py-[13px]",
        human ? "border-dashed border-[#8E939A] bg-white" : "border-nv-brand bg-nv-brand-subtle",
        selected && (compact ? "border-2 px-[14px] py-3" : wide ? "border-2 px-[14px] py-3" : "border-2 px-3 py-3"),
      )}
    >
      <span className={cn("text-[13px] leading-[21px]", human ? "text-nv-text-secondary" : "text-nv-brand-text", selected && "text-[12px] font-bold leading-5")}>{step.code}</span>
      <span className={cn("line-clamp-2 break-keep leading-[21px] tracking-[-0.011em] text-nv-text-primary", selected ? "text-[14px] font-normal leading-[22px]" : "text-[15px] font-medium")}>{step.name}</span>
      {human && (
        <span className="inline-flex h-[23px] items-center gap-1 rounded-full border border-[#C9CBCF] bg-white px-[9px] text-[12px] leading-[19px] text-nv-text-secondary">
          <NvUserSmallIcon size={12} />
          사람 {step.hitl}
        </span>
      )}
      {step.loop_back.length > 0 && <span className="text-[11px] leading-[14px] text-nv-text-tertiary">↩ {step.loop_back.join(", ")}번 단계로 되돌아감</span>}
    </button>
  );
}

// 병렬 그룹 박스: `2 · 병렬 수행 4` 칩 + 2열 격자, 4개를 넘으면 '+N 더보기' (전체 보기 팝업은 모두 펼침)
function ParallelGroup({ major, steps, selected, onSelect, expandAll }: { major: string; steps: WorkflowStep[]; selected: string; onSelect: (code: string) => void; expandAll?: boolean }) {
  const [open, setOpen] = useState(false);
  const overflow = steps.length > GROUP_VISIBLE;
  const showAll = expandAll || open || !overflow || steps.slice(GROUP_VISIBLE).some((s) => s.code === selected);
  const visible = showAll ? steps : steps.slice(0, GROUP_VISIBLE);
  return (
    <div role="group" aria-label={`${major}단계 병렬 수행 ${steps.length}`} className={cn("flex flex-col gap-[10px] rounded-[14px] border border-[#F7B48F] bg-white px-[15px] pb-[15px] pt-[13px]", expandAll ? "w-[390px] shrink-0" : "w-full min-w-0")}>
      <span className="inline-flex h-[22px] items-center self-start rounded-[6px] bg-[#FFF0E8] px-[10px] text-[12px] font-bold leading-[14px] text-nv-brand-text">
        {major} · 병렬 수행 {steps.length}
      </span>
      <div className="grid grid-cols-2 gap-2">
        {visible.map((step) => (
          <StepNode key={step.code} step={step} selected={step.code === selected} onSelect={() => onSelect(step.code)} compact />
        ))}
      </div>
      {overflow && !expandAll && (
        <button
          type="button"
          aria-expanded={showAll}
          onClick={() => setOpen((v) => !v)}
          className="h-8 rounded-nv-md border border-dashed border-[#F7B48F] text-[12px] font-bold leading-[14px] text-nv-brand-text hover:bg-nv-brand-subtle"
        >
          {showAll ? "접기" : `+${steps.length - GROUP_VISIBLE} 더보기`}
        </button>
      )}
    </div>
  );
}

// `a, b (c, d), e` → 쉼표(괄호 밖)로 나눈 항목
function splitList(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let buf = "";
  for (const ch of text) {
    if ("([（".includes(ch)) depth += 1;
    else if (")]）".includes(ch) && depth > 0) depth -= 1;
    if (ch === "," && depth === 0) {
      parts.push(buf);
      buf = "";
    } else buf += ch;
  }
  parts.push(buf);
  return parts.map((p) => p.trim()).filter(Boolean);
}

function Field({ label, children, chips }: { label: string; children: string; chips?: boolean }) {
  // INPUT · OUTPUT이 둘 이상의 쉼표 목록이면 회색 칩으로 (Figma ver.7 03-00), 하나면 문장 그대로 (03-04)
  const items = chips ? splitList(children) : [];
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[13px] leading-[21px] text-nv-text-tertiary">{label}</span>
      {items.length >= 2 ? (
        <ul className="flex flex-wrap gap-2 pt-[2px]">
          {items.map((item, i) => (
            <li key={`${item}-${i}`} className="inline-flex min-h-6 items-center rounded-[6px] bg-[#F3F4F6] px-[10px] py-[3px] text-[13px] font-medium leading-4 text-nv-text-primary">
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="whitespace-pre-line text-[15px] leading-6 text-nv-text-primary">{children}</p>
      )}
    </div>
  );
}

// 세부단계 상세: 하는 일 / INPUT / OUTPUT (+ 2차까지는 도입 후 처리 내용), 빈 항목은 그 줄만 숨겨요.
export function StepDetail({ step, className }: { step: WorkflowStep; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-4 rounded-nv-card border border-nv-border bg-nv-surface p-[23px] md:flex-row md:gap-6", className)}>
      <h4 className="w-full shrink-0 text-[17px] leading-[27px] text-nv-text-primary md:w-[208px]">
        <span className="mr-[6px] text-nv-brand-text">{step.code}</span>
        {step.name}
      </h4>
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {step.does && <Field label="하는 일">{step.does}</Field>}
        {step.input && (
          <Field label="INPUT" chips>
            {step.input}
          </Field>
        )}
        {step.output && (
          <Field label="OUTPUT" chips>
            {step.output}
          </Field>
        )}
        {step.after && <Field label="도입 후 처리 내용">{step.after}</Field>}
      </div>
    </div>
  );
}

function StageHeader({ stage }: { stage: WorkflowStage }) {
  return (
    <div className="flex flex-wrap items-center gap-x-[10px] gap-y-1">
      <span className="inline-flex h-5 items-center rounded-[6px] border border-nv-border-strong bg-white px-[10px] text-[12px] font-bold leading-[14px] text-nv-text-primary">{stage.no}단계</span>
      <span className="text-[14px] font-medium leading-[17px] text-nv-text-primary">{stage.name}</span>
      {stage.description && <span className="text-[13px] leading-4 text-nv-text-secondary">{stage.description}</span>}
    </div>
  );
}

const INLINE_COLUMNS = 5; // 한 줄 5칸 (Figma ver.7: 카드 166.4 · 칸 사이 62 · 화살표는 칸 사이 가운데)
const INLINE_CARD = 166;

// 인라인 한 줄: 칸은 최대 5개(병렬 그룹은 2칸) 격자로 폭을 나누고, 5칸을 넘으면 칸 폭을 166으로 고정한 채 가로로 스크롤해요 (카드 제목 2줄 넘으면 말줄임)
function InlineRow({ cells, selected, onSelect }: { cells: Cell[]; selected: string; onSelect: (code: string) => void }) {
  const spans = cells.map((cell) => (cell.kind === "group" ? 2 : 1));
  const total = spans.reduce((a, b) => a + b, 0);
  const columns = total <= INLINE_COLUMNS ? `repeat(${INLINE_COLUMNS}, minmax(140px, 1fr))` : `repeat(${total}, ${INLINE_CARD}px)`;
  return (
    <div className="overflow-x-auto">
      {/* 병렬 그룹이 없으면 같은 줄 카드 높이를 맞추고, 있으면 가운데 정렬 (카드 높이는 내용에 맞춤) */}
      <div className={cn("grid gap-x-[62px]", cells.some((cell) => cell.kind === "group") ? "items-center" : "items-stretch")} style={{ gridTemplateColumns: columns }}>
        {cells.map((cell, i) => (
          <div key={cell.kind === "group" ? `g${cell.major}` : cell.step.code} className="relative flex min-w-0" style={{ gridColumn: `span ${spans[i]}` }}>
            {cell.kind === "group" ? (
              <ParallelGroup major={cell.major} steps={cell.steps} selected={selected} onSelect={onSelect} />
            ) : (
              <StepNode step={cell.step} selected={cell.step.code === selected} onSelect={() => onSelect(cell.step.code)} />
            )}
            {i < cells.length - 1 && <Arrow className="absolute left-full top-1/2 ml-[16.5px] -translate-y-1/2" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function StageBand({ stage, showHeader, selected, onSelect, wide, detail = true }: { stage: WorkflowStage; showHeader: boolean; selected: string; onSelect: (code: string) => void; wide?: boolean; detail?: boolean }) {
  const cells = useMemo(() => cellsOf(stage.steps), [stage.steps]);
  // 팝업은 4개씩 줄바꿈 (잘림 없이 한 화면)
  const rows = useMemo(() => chunkCells(cells, POPUP_PER_ROW), [cells]);
  const selectedStep = stage.steps.find((s) => s.code === selected);
  return (
    <div className="flex flex-col gap-[14px]">
      {showHeader && <StageHeader stage={stage} />}
      {wide ? (
        rows.map((row, r) => (
          <div key={r} className="flex items-center gap-2">
            {row.map((cell, c) => (
              <div key={cell.kind === "group" ? `g${cell.major}` : cell.step.code} className="flex items-center gap-2">
                {cell.kind === "group" ? (
                  <ParallelGroup major={cell.major} steps={cell.steps} selected={selected} onSelect={onSelect} expandAll />
                ) : (
                  <StepNode step={cell.step} selected={cell.step.code === selected} onSelect={() => onSelect(cell.step.code)} wide />
                )}
                {c < row.length - 1 && <Arrow />}
              </div>
            ))}
          </div>
        ))
      ) : (
        <InlineRow cells={cells} selected={selected} onSelect={onSelect} />
      )}
      {detail && selectedStep && <StepDetail step={selectedStep} className="mt-[6px]" />}
    </div>
  );
}

export function WorkflowDiagram({ stages, hitlChips, hitlNote, popup }: { stages: WorkflowStage[]; hitlChips: string[]; hitlNote: string | null; popup?: boolean }) {
  const first = stages[0]?.steps[0]?.code ?? "";
  const [selected, setSelected] = useState(first);
  const showHeader = stages.length > 1 || !!popup;
  const selectedStep = stages.flatMap((s) => s.steps).find((s) => s.code === selected);
  return (
    <div className="relative flex flex-col gap-5 rounded-[20px] bg-nv-bg-page p-5 xl:p-7">
      {/* 범례는 인라인에서는 제목 줄 오른쪽(WorkflowSection), 팝업은 도식 상자 안 위 */}
      {popup && <WorkflowLegend />}
      <div className="flex flex-col gap-4">
        {stages.map((stage, i) => (
          <div key={stage.no} className={cn(i > 0 && "border-t border-dashed border-nv-border-strong pt-5")}>
            <StageBand stage={stage} showHeader={showHeader} selected={selected} onSelect={setSelected} wide={popup} detail={!popup} />
          </div>
        ))}
      </div>
      {popup && selectedStep && <StepDetail step={selectedStep} />}
      {(hitlChips.length > 0 || hitlNote) && (
        <div className={cn("flex gap-[11px]", popup ? "flex-wrap items-center" : "flex-col")}>
          <h4 className="text-[14px] leading-[22px] text-nv-text-primary">사람 개입 지점</h4>
          {hitlChips.length > 0 && (
            <div className="flex flex-wrap gap-[6px]">
              {hitlChips.map((chip) => (
                <span key={chip} className="inline-flex h-[22px] items-center rounded-[6px] border border-nv-border bg-white px-[10px] text-[13px] leading-4 text-nv-text-secondary">
                  {chip}
                </span>
              ))}
            </div>
          )}
          {hitlNote && <p className="whitespace-pre-line text-[14px] leading-[22px] text-nv-text-secondary">{hitlNote}</p>}
        </div>
      )}
    </div>
  );
}

// 전체 보기 팝업 (Figma 546:537 → ver.7 847:44770): 모든 단계 띠를 큰 도식(카드 252, 한 줄 4개)으로 펼치고 아래에 선택한 세부단계 상세 · 사람 개입 지점. 단계 수와 무관하게 잘림 없이 보여줘요.
// 닫기 아이콘은 Lucide x 24px (ver.7 06)
export function WorkflowFullDialog({ open, onOpenChange, agentName, stages, hitlChips, hitlNote }: { open: boolean; onOpenChange: (open: boolean) => void; agentName: string; stages: WorkflowStage[]; hitlChips: string[]; hitlNote: string | null }) {
  return (
    <NvModal open={open} onOpenChange={onOpenChange} title={`워크플로우 전체 보기 · ${agentName}`} width={1280} closeIcon={<NvLucideCloseIcon size={24} />}>
      <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-1">
        <WorkflowDiagram stages={stages} hitlChips={hitlChips} hitlNote={hitlNote} popup />
      </div>
    </NvModal>
  );
}
